import type { DocType } from '@eriko/shared-types';
import { useCallback, useState } from 'react';
import { presignUpload, uploadToPresignedUrl } from '../api/upload';
import { guessDocType } from '../pages/documents/guessDocType';

const ALLOWED_EXTENSIONS: Record<string, UploadingFile['fileType']> = {
  pdf: 'PDF',
  csv: 'CSV',
  jpg: 'IMAGE',
  jpeg: 'IMAGE',
  png: 'IMAGE',
  webp: 'IMAGE',
  heic: 'IMAGE',
};

export interface UploadingFile {
  id: string;
  file: File;
  fileName: string;
  fileType: 'PDF' | 'CSV' | 'IMAGE';
  progress: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
  documentId?: string;
  uploadId?: string; // legacy alias (== documentId)
  fileUrl?: string;
  fileKey?: string;
}

export interface FileReference {
  uploadId: string;
  documentId?: string;
  fileKey?: string;
  fileName: string;
  fileType: string;
  fileUrl: string;
}

export interface UseFileUploadOptions {
  /** Chat upload passes the current conversation id; page upload leaves null. */
  linkedConversationId?: string | null;
}

function getFileType(file: File): UploadingFile['fileType'] | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ALLOWED_EXTENSIONS[ext] ?? null;
}

function generateId(): string {
  return `file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const { linkedConversationId = null } = options;
  const [files, setFiles] = useState<UploadingFile[]>([]);

  const updateFile = useCallback((id: string, updates: Partial<UploadingFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  }, []);

  const uploadSingleFile = useCallback(
    async (uploadingFile: UploadingFile, docTypeOverride?: DocType) => {
      try {
        const docType =
          docTypeOverride ?? guessDocType(uploadingFile.fileName, uploadingFile.file.type);
        const presign = await presignUpload({
          fileName: uploadingFile.fileName,
          fileType: uploadingFile.fileType,
          fileSize: uploadingFile.file.size,
          docType,
          linkedConversationId: linkedConversationId ?? undefined,
        });

        updateFile(uploadingFile.id, {
          documentId: presign.documentId,
          uploadId: presign.documentId,
          fileUrl: presign.fileUrl,
          fileKey: presign.fileKey,
        });

        await uploadToPresignedUrl(presign.uploadUrl, uploadingFile.file, (percent) =>
          updateFile(uploadingFile.id, { progress: percent }),
        );

        updateFile(uploadingFile.id, { status: 'done', progress: 100 });
        return presign;
      } catch (err) {
        updateFile(uploadingFile.id, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
        throw err;
      }
    },
    [updateFile, linkedConversationId],
  );

  const addFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const newFiles: UploadingFile[] = [];

      for (const file of Array.from(fileList)) {
        const fileType = getFileType(file);
        if (!fileType) {
          const entry: UploadingFile = {
            id: generateId(),
            file,
            fileName: file.name,
            fileType: 'PDF',
            progress: 0,
            status: 'error',
            error: `Unsupported file type: ${file.name.split('.').pop()}`,
          };
          newFiles.push(entry);
          continue;
        }

        newFiles.push({
          id: generateId(),
          file,
          fileName: file.name,
          fileType,
          progress: 0,
          status: 'uploading',
        });
      }

      setFiles((prev) => [...prev, ...newFiles]);

      const toUpload = newFiles.filter((f) => f.status === 'uploading');
      await Promise.all(toUpload.map((f) => uploadSingleFile(f).catch(() => {})));
    },
    [uploadSingleFile],
  );

  /**
   * Upload a single file with an explicit docType (used by the Documents
   * page UploadDialog which collects the type upfront).
   * Adds a transient row to `files` for progress tracking; returns the
   * presign payload so callers can capture documentId/fileUrl.
   */
  const uploadOne = useCallback(
    async (file: File, opts: { docType: DocType }) => {
      const fileType = getFileType(file);
      if (!fileType) {
        throw new Error(`Unsupported file type: ${file.name.split('.').pop()}`);
      }
      const uploading: UploadingFile = {
        id: generateId(),
        file,
        fileName: file.name,
        fileType,
        progress: 0,
        status: 'uploading',
      };
      setFiles((prev) => [...prev, uploading]);
      return uploadSingleFile(uploading, opts.docType);
    },
    [uploadSingleFile],
  );

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const pendingFiles = files.filter((f) => f.status === 'done');
  const isUploading = files.some((f) => f.status === 'uploading');

  return {
    files,
    addFiles,
    uploadOne,
    removeFile,
    clearFiles,
    pendingFiles,
    isUploading,
  };
}
